import { unwrap } from "@sagot/api-client";
import { CHECKOUT_REF_PARAM, CHECKOUT_RESULT_PARAM, SETUP_GATEWAY_PARAM, SETUP_RESULT_PARAM } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Dialog, DialogContent, Icon, toast } from "@sagot/ui";
import { useMutation } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import { useErrorMessage } from "../../../lib/api-errors";
import { useApi } from "../../../session/session-provider";
import { useFormatters, type OrderOut } from "../shared";
import { checkoutAction } from "./billing-model";
import { useCancelPayment, useConfirmPayment } from "./confirm-payment";
import { takeSetup, useCompleteSetup } from "./method-setup";
import { useInvalidateBilling } from "./use-billing";

interface PendingPayment {
  paymentId: string;
  amount: string | null;
  currency: string;
  success: string;
}

/** Follows an order; `success` is the toast once paid (defaults to a generic "payment received"). */
export type HandleOrder = (order: OrderOut, success?: string) => void;

const CheckoutContext = createContext<HandleOrder | null>(null);

/** Billing page: the order handler provided by `CheckoutProvider`. */
export function useHandleOrder(): HandleOrder {
  const value = useContext(CheckoutContext);
  if (!value) throw new Error("CheckoutProvider is missing");
  return value;
}

function DevCheckoutDialog({ pending, onClose }: { pending: PendingPayment; onClose: () => void }) {
  const { t } = useTranslation();
  const api = useApi();
  const fmt = useFormatters();
  const message = useErrorMessage();
  const invalidate = useInvalidateBilling();
  const complete = useMutation({
    mutationFn: (outcome: "paid" | "failed") =>
      unwrap(api.POST("/v1/billing/dev/payments/{payment_id}/complete", { params: { path: { payment_id: pending.paymentId } }, body: { outcome } })),
    onSuccess: async (_data, outcome) => {
      if (outcome === "paid") toast.success(pending.success);
      else toast.warning(t("billing.checkout.failed"));
      await invalidate();
      onClose();
    },
    onError: (caught) => toast.error(message(caught)),
  });
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        title={t("billing.checkout.devTitle")}
        description={t("billing.checkout.devBody")}
        closeLabel={t("common.close")}
        className="max-w-md"
        footer={
          <>
            <Button variant="secondary" loading={complete.isPending && complete.variables === "failed"} disabled={complete.isPending} onClick={() => { complete.mutate("failed"); }}>
              {t("billing.checkout.devFail")}
            </Button>
            <Button loading={complete.isPending && complete.variables === "paid"} disabled={complete.isPending} onClick={() => { complete.mutate("paid"); }}>
              <Icon name="check" />
              {t("billing.checkout.devPay")}
            </Button>
          </>
        }
      >
        <div className="flex items-center justify-between rounded-md bg-surface-2 p-3 text-sm">
          <span className="text-fg-muted">{t("billing.checkout.amount")}</span>
          <span className="font-bold tabular-nums">{pending.amount ? fmt.moneyExact(pending.amount, pending.currency) : "—"}</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const RETURN_PARAMS = [CHECKOUT_RESULT_PARAM, CHECKOUT_REF_PARAM, SETUP_RESULT_PARAM, SETUP_GATEWAY_PARAM] as const;

/** PSP return URLs (`?checkout=success&ref=…`, `?setup=success&gateway=…`), then strips them. */
function useCheckoutReturn() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const confirm = useConfirmPayment();
  const cancel = useCancelPayment();
  const completeSetup = useCompleteSetup();
  const result = params.get(CHECKOUT_RESULT_PARAM);
  const ref = params.get(CHECKOUT_REF_PARAM);
  const setup = params.get(SETUP_RESULT_PARAM);
  const gateway = params.get(SETUP_GATEWAY_PARAM);
  const { mutate: confirmPayment } = confirm;
  const { mutate: cancelPayment } = cancel;
  const { mutate: finishSetup } = completeSetup;
  const handled = useRef<string | null>(null);
  useEffect(() => {
    if (!result && !setup) return;
    const key = [result, ref, setup, gateway].join("|");
    if (handled.current === key) return; // StrictMode re-run: the return is handled once
    handled.current = key;
    if (result === "success" && ref) confirmPayment(ref);
    else if (result === "success") toast.success(t("billing.checkout.returnSuccess"));
    else if (result) {
      toast.info(t("billing.checkout.returnCancelled"));
      if (ref) cancelPayment(ref);
    }
    if (setup === "success") {
      const pending = takeSetup(gateway);
      if (pending) finishSetup(pending);
      else toast.error(t("billing.methods.setupLost"));
    } else if (setup) toast.info(t("billing.methods.setupCancelled"));
    setParams((current) => {
      const next = new URLSearchParams(current);
      for (const name of RETURN_PARAMS) next.delete(name);
      return next;
    }, { replace: true });
  }, [result, ref, setup, gateway, confirmPayment, cancelPayment, finishSetup, setParams, t]);
}

/**
 * Reusable order handling (Billing, Team "Add seats"): PSP redirect, in-app test checkout for
 * MockPayments (dev), or an instant result. Render `dialog` once next to the trigger.
 */
export function useOrderHandler(): { handle: HandleOrder; dialog: ReactNode } {
  const { t } = useTranslation();
  const invalidate = useInvalidateBilling();
  const [pending, setPending] = useState<PendingPayment | null>(null);
  const handle = useCallback<HandleOrder>(
    (order, success) => {
      const message = success ?? t("billing.checkout.paid");
      const action = checkoutAction(order);
      if (action.kind === "dev") setPending({ paymentId: action.paymentId, amount: order.amount ?? null, currency: order.currency ?? "PHP", success: message });
      else if (action.kind === "redirect") {
        toast.info(t("billing.checkout.redirecting"));
        window.location.assign(action.url);
      } else if (action.kind === "failed") {
        toast.warning(t("billing.checkout.failed"));
        void invalidate();
      } else if (action.kind === "charged" || action.kind === "scheduled") {
        toast.success(message);
        void invalidate();
      } else toast.error(t("billing.checkout.unavailable"));
    },
    [invalidate, t],
  );
  const dialog = pending ? <DevCheckoutDialog pending={pending} onClose={() => { setPending(null); }} /> : null;
  return { handle, dialog };
}

export function CheckoutProvider({ children }: { children: ReactNode }) {
  const { handle, dialog } = useOrderHandler();
  useCheckoutReturn();
  return (
    <CheckoutContext.Provider value={handle}>
      {children}
      {dialog}
    </CheckoutContext.Provider>
  );
}
